import React, { useState, useEffect, useRef } from "react";
import {
  Select,
  Input,
  Button,
  Card,
  Typography,
  Space,
  Row,
  Col,
  Spin,
  Tooltip,
  Tabs,
  Popconfirm,
} from "antd";
import { transformString } from "../../../helpers/channelNameTransformation";
import "./promptEditor.css";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

export default function PromptEditor({
  promptsData,
  updatePrompt,
  loadingUpdate,
}) {
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [editableData, setEditableData] = useState({
    name: "",
    model: "",
    text: "",
    description: "",
  });
  const [currentVars, setCurrentVars] = useState({});
  const [highlightPositions, setHighlightPositions] = useState({});
  const textAreaRef = useRef();
  const selectedPrompt = promptsData?.find(
    (prompt) => prompt.id === selectedPromptId
  );
  const selectedPromptName =
    selectedPrompt?.label ||
    transformString(selectedPrompt?.name || "") ||
    "this prompt";

  // Function to find all positions of substring in text
  const findPositions = (text, substring) => {
    const positions = [];
    let index = text.indexOf(substring);
    while (index !== -1) {
      positions.push([index, index + substring.length]);
      index = text.indexOf(substring, index + 1);
    }
    return positions;
  };

  // Handle variable click to highlight in text
  const handleVarClick = (key) => {
    const substring = `{${key}}`;
    const positions = findPositions(editableData.text, substring);
    if (positions.length > 0) {
      const current = highlightPositions[key]?.current || 0;
      const next = (current + 1) % positions.length;
      setHighlightPositions(prev => ({
        ...prev,
        [key]: { positions, current: next }
      }));
      const [start, end] = positions[next];
      if (textAreaRef.current && textAreaRef.current.resizableTextArea) {
        const textarea = textAreaRef.current.resizableTextArea.textArea;
        textarea.focus();
        textarea.setSelectionRange(start, end);
        // Scroll to make the selection visible
        setTimeout(() => {
          const textLength = editableData.text.length;
          const scrollPosition = (start / textLength) * textarea.scrollHeight - textarea.clientHeight / 2;
          textarea.scrollTop = Math.max(0, scrollPosition);
        }, 0);
      }
    }
  };

  // Initialize state when promptsData loads
  useEffect(() => {
    if (promptsData && promptsData.length > 0 && !selectedPromptId) {
      const firstPrompt = promptsData[0];
      setSelectedPromptId(firstPrompt.id);
      setEditableData({
        name: firstPrompt.name || "",
        model: firstPrompt.model || "",
        text: firstPrompt.text || "",
        description: firstPrompt.description || "",
      });
      setCurrentVars(firstPrompt.vars || {});
    }
  }, [promptsData, selectedPromptId]);

  const handleSelectChange = (value) => {
    const selectedPrompt = promptsData.find((prompt) => prompt.id === value);
    setSelectedPromptId(value);
    setEditableData({
      name: selectedPrompt?.name || "",
      model: selectedPrompt?.model || "",
      text: selectedPrompt?.text || "",
      description: selectedPrompt?.description || "",
    });
    setCurrentVars(selectedPrompt?.vars || {});
  };

  const handleInputChange = (field, value) => {
    setEditableData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = () => {
    const data = {
      id: selectedPromptId,
      ...editableData,
    };

    updatePrompt(data);
  };

  // Show loading state if no data
  if (!promptsData || promptsData.length === 0) {
    return (
      <div
        style={{
          padding: "24px",
          width: "70%",
          margin: "0 auto",
        }}
      >
        <Card
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "500px",
            background: "none",
          }}
        >
          <Spin spinning={true} />
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <Card
        title={
          <Title level={2} style={{ margin: 0 }}>
            Prompt Editor
          </Title>
        }
        style={{
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{
            width: "100%",
          }}
        >
          {/* Two-column layout for Prompt/Model and Description */}
          <Row gutter={[24, 16]} align="stretch">
            {/* Left Column: Select Prompt and Model */}
            <Col xs={24} md={12}>
              <Card
                bordered={false}
                className="prompt-card"
                style={{
                  height: "100%",
                  // backgroundColor: "#fafafa",
                  borderRadius: "8px",
                  padding: "0",
                }}
                bodyStyle={{
                  height: "100%",
                  padding: "20px",
                }}
              >
                {/* Select Dropdown */}
                <div style={{ marginBottom: "24px" }}>
                  <Text
                    strong
                    style={{
                      fontSize: "16px",
                      marginBottom: "8px",
                      display: "block",
                      color: "#333",
                    }}
                  >
                    Select Prompt:
                  </Text>
                  <Select
                    value={selectedPromptId}
                    onChange={handleSelectChange}
                    style={{ width: "100%" }}
                    size="large"
                    dropdownStyle={{ borderRadius: "6px" }}
                  >
                    {promptsData.map((prompt) => (
                      <Option key={prompt.id} value={prompt.id}>
                        {prompt?.label || transformString(prompt?.name)}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Model Input */}
                <div>
                  <Text
                    strong
                    style={{
                      fontSize: "16px",
                      marginBottom: "8px",
                      display: "block",
                      color: "#333",
                    }}
                  >
                    Model:
                  </Text>
                  <Input
                    value={editableData?.model}
                    onChange={(e) => handleInputChange("model", e.target.value)}
                    placeholder="Enter model"
                    size="large"
                    style={{ borderRadius: "6px" }}
                  />
                </div>
              </Card>
            </Col>

            {/* Right Column: Description and Variables */}
            <Col xs={24} md={12}>
              <Tabs
                defaultActiveKey="variables"
                style={{ width: "100%" }}
                items={[
                  {
                    key: "variables",
                    label: `Available Variables (${Object.keys(currentVars).length})`,
                    children: (
                      <Card
                        bordered={false}
                        style={{
                          borderRadius: "8px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                          border: "1px solid #f0f0f0",
                          background: "linear-gradient(to bottom, #fafafa 0%, #ffffff 100%)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10px",
                            padding: "4px 0",
                          }}
                          className="variables-container"
                        >
                          {Object.keys(currentVars).map((key) => {
                            const isUsed = editableData.text.includes(`{${key}}`);
                            return (
                              <Tooltip
                                key={key}
                                overlayClassName="variable-tooltip"
                                overlayInnerStyle={{
                                  maxHeight: '200px',
                                  maxWidth: '400px',
                                  overflow: 'auto',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                                title={currentVars[key]}
                              >
                                <Button
                                  size="middle"
                                  style={{
                                    backgroundColor: isUsed ? '#e6f4ff' : '#f5f5f5',
                                    color: isUsed ? '#0958d9' : '#8c8c8c',
                                    borderColor: isUsed ? '#91caff' : '#d9d9d9',
                                    cursor: isUsed ? 'pointer' : 'default',
                                    whiteSpace: 'nowrap',
                                    fontWeight: '500',
                                    borderRadius: '6px',
                                    boxShadow: isUsed ? '0 1px 2px rgba(9,88,217,0.1)' : 'none',
                                    transition: 'all 0.3s ease',
                                  }}
                                  onMouseEnter={isUsed ? (e) => {
                                    e.currentTarget.style.backgroundColor = '#bae0ff';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(9,88,217,0.15)';
                                  } : undefined}
                                  onMouseLeave={isUsed ? (e) => {
                                    e.currentTarget.style.backgroundColor = '#e6f4ff';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(9,88,217,0.1)';
                                  } : undefined}
                                  onClick={isUsed ? () => handleVarClick(key) : undefined}
                                >
                                  {key}
                                </Button>
                              </Tooltip>
                            );
                          })}
                        </div>
                        <div style={{ marginTop: "20px" }}>
                          <Text strong style={{ fontSize: "14px", color: "#333", marginBottom: "8px", display: "block" }}>
                            Index
                          </Text>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "12px", height: "12px", backgroundColor: "#bae0ff", border: "1px solid #69b1ff", borderRadius: "2px" }}></div>
                                <Text type="secondary">Used in prompt</Text>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "12px", height: "12px", backgroundColor: "#fafafa", border: "1px solid #bfbfbf", borderRadius: "2px" }}></div>
                                <Text type="secondary">Not used in prompt</Text>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ),
                  },
                  {
                    key: "description",
                    label: "Description",
                    children: (
                      <Card
                        bordered={false}
                        style={{
                          borderRadius: "8px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <TextArea
                          value={editableData?.description || ""}
                          onChange={(e) =>
                            handleInputChange("description", e.target.value)
                          }
                          placeholder="Enter description here..."
                          autoSize={{ minRows: 6, maxRows: 10 }}
                          style={{
                            padding: "12px",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            fontSize: "14px",
                            lineHeight: "1.6",
                            minHeight: "120px",
                            whiteSpace: "pre-wrap",
                            border: "1px solid #e8e8e8",
                            overflow: "auto",
                          }}
                        />
                      </Card>
                    ),
                  },
                ]}
              />
            </Col>
          </Row>

          {/* Prompt Content Field - Full Width */}
          <Card
            bordered={false}
            title={
              <Text
                strong
                style={{
                  fontSize: "16px",
                  color: "#333",
                  margin: 0,
                }}
              >
                Prompt Content:
              </Text>
            }
            style={{
              borderRadius: "8px",
            }}
          >
            <TextArea
              ref={textAreaRef}
              value={editableData?.text}
              onChange={(e) => handleInputChange("text", e.target.value)}
              placeholder="Enter prompt text here..."
              autoSize={{ minRows: 12, maxRows: 25 }}
              style={{
                borderRadius: "6px",
                fontSize: "14px",
                lineHeight: "1.6",
                backgroundColor: "white",
                border: "1px solid #e8e8e8",
              }}
            />
          </Card>

          {/* Save Button */}
          <div style={{ textAlign: "center", paddingTop: "16px" }}>
            <Popconfirm
              title={`Save changes on ${selectedPromptName}?`}
              okText="Save"
              cancelText="Cancel"
              onConfirm={handleSaveChanges}
              disabled={loadingUpdate || !selectedPromptId}
            >
              <Button
                type="primary"
                size="large"
                style={{
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                  borderRadius: "6px",
                  padding: "8px 32px",
                  height: "auto",
                  fontSize: "16px",
                  fontWeight: "600",
                  boxShadow: "0 2px 0 rgba(0,0,0,0.045)",
                }}
                loading={loadingUpdate}
                disabled={loadingUpdate}
              >
                Save Changes
              </Button>
            </Popconfirm>
          </div>
        </Space>
      </Card>
    </div>
  );
}
